import type { ServicePageConfig } from "../../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../../package-pricing";
import { getCityMunicipalityList } from "../../cities";

const goteborgKommunerText = getCityMunicipalityList("göteborg");

export const partillePages: ServicePageConfig[] = [
  {
    slug: "bortforsling-partille",
    serviceCategory: "bortforsling",
    city: "göteborg",

    meta: {
      title: "Bortforsling Partille – Snabb Hämtning | Ungdomskraft",
      description:
        "Boka bortforsling i Partille. Vi hämtar möbler, vitvaror och skräp i Partille centrum, Sävedalen, Jonsered, Öjersjö och Furulund. Fast pris, ofta samma dag.",
      h1: "Bortforsling Partille",
    },

    hero: {
      heading: "Bortforsling i Partille",
      intro:
        "Boka bortforsling i Partille – vi hämtar möbler, vitvaror och skrymmande föremål i hela kommunen, från lägenheterna i Partille centrum och Björndammen till villorna i Öjersjö, Jonsered och Sävedalen. Snabb bokning, fast pris och ofta tillgängliga redan samma dag. Tusentals slutförda uppdrag i Göteborgsregionen.",
      heroImage: "/hero-bortforsling.webp",
      heroImageAlt:
        "Ungdomskraft hämtar möbler vid bortforsling i Partille – ung medarbetare lastar skåpbil",
    },

    itemsSection: {
      heading: "Saker vi bortforslar i Partille",
      items: [
        {
          name: "Återvinning",
          description: "Glas, metall, papper och annat återvinningsbart",
          image: "/recycling-bin.webp",
          alt: "Återvinning som hämtas vid bortforsling i Partille och körs till Öjersjö återvinningscentral",
        },
        {
          name: "Kartonger",
          description: "Flyttkartonger och wellpappkartonger",
          image: "/box.webp",
          alt: "Flyttkartonger som forslas bort från villa i Öjersjö, Partille",
        },
        {
          name: "Möbler",
          description: "Vardagsrum, sovrum, kontor och andra hemmöbler",
          image: "/furniture.webp",
          alt: "Soffor och hemmöbler vid bortforsling i Partille centrum",
        },
        {
          name: "Elektronik",
          description:
            "TV-apparater, datorer, ljudsystem och annan elektronik",
          image: "/electronics.webp",
          alt: "Elektronik och TV-apparater som bortforslas i Sävedalen, Partille",
        },
        {
          name: "Vitvaror",
          description:
            "Kylskåp, tvättmaskiner, torktumlare och andra vitvaror",
          image: "/appliances.webp",
          alt: "Kylskåp och vitvaror som hämtas vid bortforsling i Björndammen, Partille",
        },
        {
          name: "Träningsutrustning",
          description:
            "Löpband, cyklar, vikter och annan träningsutrustning",
          image: "/fitness-equipment.webp",
          alt: "Löpband och träningsutrustning vid bortforsling i Jonsered, Partille",
        },
      ],
    },

    seoContent: {
      mainHeading: "Bortforsling i Partille – snabbt, smidigt och ansvarsfullt",
      mainText:
        "Partille är en liten men tätbefolkad kommun strax nordost om Göteborg, med fyra kommundelar – Partille, Sävedalen, Jonsered och Öjersjö. Bebyggelsen är blandad: lägenhetskvarteren kring Partille centrum, Björndammen och Furulund, villaområdena i Öjersjö och Sävedalen, samt den historiska bruksmiljön i Jonsered vid sjön Aspen. Den blandningen gör bortforsling till olika utmaningar: i lägenheten handlar det ofta om trapphus och hiss, medan det i villan snarare handlar om ett fullt garage eller ett källarförråd. Med Ungdomskraft slipper du allt krångel oavsett vilket. Vi skickar unga, starka medarbetare med skåpbil direkt till din adress.\n\nVårt team hanterar hela kedjan: vi bär ut föremålen från din lägenhet, villa eller ditt förråd, lastar i bilen och kör till närmaste återvinningscentral. Partille har en stor fördel här – kommunens egen återvinningscentral i Öjersjö, vid Landvettervägen, ligger mitt i kommunen och avfallet hämtas av Renova. Det gör att avfallet sällan behöver köras långt. Du behöver inte tänka på sortering, parkering eller tunga lyft. Vi tar hand om allt.\n\nTack vare att E20 går rakt genom Partille och pendeltåget mellan Göteborg och Alingsås stannar vid Partille station tar vi oss snabbt mellan kommundelarna. Det gör att vi ofta kan vara på plats redan samma dag du bokar. Våra medarbetare är vana vid både lägenhetskvarteren kring Allum och de smala villagatorna i Öjersjö och Jonsered – och har utrustningen som krävs: bärbälten, skyddsfiltar och lastramper som skyddar både dina ägodelar och trapphuset under hela processen.",
      preparationHeading: "Förbered inför din bortforsling i Partille",
      preparationText:
        "Du behöver egentligen inte göra mycket alls – våra ungdomar sköter det tunga jobbet. Men vill du snabba på processen och hålla nere kostnaden finns det några enkla saker du kan göra:\n\nRensa en fri väg (ungefär 1 meter bred) från entrén till föremålen som ska bort. Bor du i villa i Öjersjö, Jonsered, Sävedalen eller Ugglum är det smidigt om vi kan backa in på uppfarten – meddela oss om grinden eller garageporten behöver öppnas. Bor du i lägenhet i Partille centrum, Björndammen eller Furulund, kontrollera om det finns en lastzon, innergård eller gästparkering nära porten som vi kan använda för lastning.\n\nSamla gärna sakerna på ett ställe om möjligt, till exempel i hallen, garaget eller förrådet. Du behöver inte demontera möbler – det ingår i vår tjänst om det behövs. Farligt avfall som färgrester, batterier och kemikalier kan vi tyvärr inte ta med, men sådant lämnar du gratis som Partillebo på Öjersjö återvinningscentral.",
      additionalSections: [
        {
          heading: "Kommundelar och områden vi täcker i Partille",
          content:
            "Vi utför bortforsling i hela Partille kommun – från lägenhetskvarteren till villaområdena och bruksmiljöerna. Oavsett om du bor i en lägenhet i Partille centrum, en villa i Öjersjö, ett radhus i Sävedalen eller ett hus i Jonsered kan vi vara på plats, ofta redan samma dag.\n\nI Partille centrum – kring köpcentret Allum, Björndammen och Furulund – har vi stor vana vid lägenhetshämtningar med hiss och trapphus, nära station och bussterminal. I Sävedalen, som gränsar mot Göteborg, möter vi en blandning av villor och flerbostadshus.\n\nVi är minst lika snabba i kommunens östra och södra delar: Jonsered med sin historiska bruksmiljö och Jonsereds fabriker vid Aspen, samt Öjersjö med villabebyggelse nära sjön Kåsjön, liksom Lexby, Ugglum och Kåhög. Tack vare närheten till E20 och pendeltåget planerar vi alltid rutten i förväg så att du slipper vänta. Inga extra avgifter för någon kommundel.",
        },
        {
          heading: "Återvinning och miljöansvar i Partille",
          content:
            "En viktig del av vår bortforsling i Partille är att allt hanteras på ett miljövänligt sätt. Vi sorterar materialet och kör det till godkända återvinningscentraler. Partille har en stor fördel eftersom kommunen har en egen station mitt i kommunen:\n\nÖjersjö återvinningscentral vid Landvettervägen – Partilles egen återvinningscentral, där sorterat grovavfall, elavfall, förpackningar och farligt avfall tas emot, liksom föremål för återbruk. Avfallshämtningen i kommunen sköts av Renova. Eftersom Öjersjö ligger centralt i kommunen håller vi restiden – och därmed kostnaden – nere.\n\nDet som kan återanvändas skänker vi vidare till organisationer som Stadsmissionen och Emmaus. På så sätt bidrar din bortforsling till cirkulär ekonomi – sakerna får ett nytt liv istället för att hamna på tippen.\n\nOm sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
        },
        {
          heading: "Varför boka bortforsling i Partille med Ungdomskraft?",
          listItems: [
            {
              label: "Lokal kunskap –",
              text: "Vi känner Partilles kommundelar, från lägenhetskvarteren kring Allum och Björndammen till villatomterna i Öjersjö och Jonsered, och vet att Öjersjö återvinningscentral ligger centralt. Det gör att vi planerar effektiva rutter och håller nere kostnaden för dig.",
            },
            {
              label: "Snabb tillgänglighet –",
              text: "Vi har skåpbilar och personal i hela Göteborgsregionen och E20 går rakt genom Partille. Ofta kan vi vara på plats redan samma dag du bokar – även på kvällar och helger.",
            },
            {
              label: "Fast pris utan överraskningar –",
              text: "Du ser priset direkt vid bokning. Inga dolda avgifter, inga tilläggsavgifter för någon kommundel i Partille, och inga offertförfrågningar. Boka på två minuter.",
            },
            {
              label: "Försäkrat och tryggt –",
              text: "Alla uppdrag är försäkrade genom Trygg Hansa. Ditt trapphus, din villauppfart, dina väggar och dina ägodelar är skyddade under hela processen.",
            },
            {
              label: "Miljövänlig hantering –",
              text: "Vi sorterar och återvinner allt vi kan via Öjersjö återvinningscentral. Det som går att donera skänks vidare. Du kan ha rätt till RUT-avdrag för transport till återbruk.",
            },
          ],
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar bortforsling i Partille?",
        answer:
          "Priset beror på volym, vikt och tid. Vår minsta kategori (Extra Liten) startar på 249 kr i grundavgift, och ett genomsnittligt uppdrag kostar runt 499–2 495 kr beroende på mängden. Du ser det exakta priset direkt vid bokning – inga dolda avgifter och inga tillägg för någon kommundel i Partille.",
      },
      {
        question: "Kan jag få RUT-avdrag på bortforsling i Partille?",
        answer:
          "Om sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
      },
      {
        question: "Hur snabbt kan ni komma för bortforsling i Partille?",
        answer:
          "Vi har skåpbilar och personal i hela Göteborgsregionen och E20 går rakt genom Partille, så vi tar oss snabbt mellan kommundelarna. Ofta kan vi vara på plats redan samma dag, och vid akuta behov ibland inom 60 minuter. Boka online och välj den tid som passar dig.",
      },
      {
        question: "Ingår det en skåpbil vid bortforsling i Partille?",
        answer:
          "Ja, vi kommer alltid med skåpbil och personal. Du behöver inte ordna eget fordon – vi sköter allt från bärning till transport och bortkörning till närmaste återvinningscentral.",
      },
      {
        question: "Kan ni bära ner saker från lägenhet i trappa i Partille?",
        answer:
          "Absolut. Vi är vana vid både trapphus och hissar i flerbostadshusen kring Partille centrum, Björndammen och Furulund. Vi bär ner möbler, vitvaror och annat oavsett våning – inga extra avgifter för trappor.",
      },
      {
        question: "Hämtar ni från villa och garage i Partille?",
        answer:
          "Ja, en stor del av våra uppdrag i Partille är villahämtningar i Öjersjö, Jonsered, Sävedalen och Ugglum. Vi backar in på uppfarten där det går och tömmer garage, källare och förråd. Meddela oss bara om en grind eller garageport behöver öppnas.",
      },
      {
        question: "Kan ni demontera möbler innan bortforsling?",
        answer:
          "Ja, vi demonterar möbler på plats om det behövs för att få ut dem genom dörrar eller nedför trappor. Det ingår i tjänsten utan extra kostnad.",
      },
      {
        question: "Är bortforslingen i Partille försäkrad?",
        answer:
          "Ja, alla uppdrag är försäkrade genom Trygg Hansa. Det innebär att din fastighet, ditt trapphus, din villauppfart och dina ägodelar är skyddade under hela processen – från lyft till transport.",
      },
      {
        question: "Vart kör ni avfallet efter bortforsling i Partille?",
        answer:
          "Vi sorterar allt material och kör det till närmaste godkända återvinningscentral. Partille har en egen station, Öjersjö återvinningscentral vid Landvettervägen, och avfallshämtningen sköts av Renova. Det som kan återanvändas skänks till Stadsmissionen eller Emmaus.",
      },
      {
        question: "Kan jag boka bortforsling i Partille på helgen?",
        answer:
          "Ja, vi erbjuder bortforsling alla dagar i veckan, inklusive lördagar och söndagar. Välj det datum och den tid som passar dig bäst direkt i vår bokning.",
      },
      {
        question: "Kan ni hämta saker från källare eller förråd i Partille?",
        answer:
          "Ja, vi hämtar föremål från alla utrymmen – källare, förråd, vindsutrymmen och garage. Vi är vana vid både villaförråden i Öjersjö och Jonsered och lägenhetsförråden i Partille centrum och Björndammen.",
      },
      {
        question: "Kan ni göra flera stopp vid bortforsling i Partille?",
        answer:
          "Ja, du kan lägga till flera hämtadresser i samma bokning. Det är praktiskt om du till exempel behöver hämta saker från både din lägenhet i Partille centrum och ditt förråd på en annan adress i kommunen.",
      },
      {
        question: "Vilka kommundelar i Partille täcker ni?",
        answer:
          "Vi utför bortforsling i hela Partille – Partille centrum, Björndammen, Furulund, Sävedalen, Jonsered och Öjersjö samt Lexby, Ugglum och Kåhög. Inga extra avgifter för någon kommundel.",
      },
      {
        question: "Tar ni hand om farligt avfall vid bortforsling i Partille?",
        answer:
          "Vi kan tyvärr inte transportera farligt avfall som färg, kemikalier, batterier eller asbest. Sådant lämnar du gratis som Partillebo på Öjersjö återvinningscentral.",
      },
      {
        question: "Vilka kommuner runt Partille utför ni bortforsling i?",
        answer: `Vi finns i hela Göteborgsregionen, inklusive följande kommuner: ${goteborgKommunerText}. Inga extra avgifter för kranskommuner.`,
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bortforsling,

    pricingRows: getPackagePricingRows("bortforsling", "sv"),

    dynamicHeadings: {
      threeSteps: "Boka din bortforsling i Partille i 3 enkla steg",
      step2Text:
        "Våra ungdomar i Partille kommer till dig och hämtar det du vill ha bortforslat. Vi hanterar tunga lyft, trånga trapphus, smala hissar och fulla villagarage – du behöver inte lyfta ett finger.",
      ctaHeading: "Boka bortforsling i Partille idag",
      ctaText:
        "Fyll i formuläret och få pris direkt – inga offertförfrågningar. Smidig bortforsling i hela Partille kommun.",
    },

    en: {
      slug: "junk-removal-partille",
      meta: {
        title: "Junk Removal Partille – Fast Pickup | Ungdomskraft",
        description:
          "Book junk removal in Partille. We pick up furniture, appliances and bulky items in Partille centrum, Sävedalen, Jonsered, Öjersjö and Furulund. Fixed price, often same day.",
        h1: "Junk Removal Partille",
      },
      hero: {
        heading: "Junk Removal in Partille",
        intro:
          "Book junk removal in Partille – we collect furniture, appliances and bulky items across the whole municipality, from the apartments in Partille centrum and Björndammen to the villas in Öjersjö, Jonsered and Sävedalen. Quick booking, fixed price and often available the same day. Thousands of completed jobs in the Gothenburg region.",
        heroImageAlt:
          "Ungdomskraft collecting furniture during junk removal in Partille – young worker loading a van",
      },
      itemsSection: {
        heading: "Items we remove in Partille",
        items: [
          {
            name: "Recycling",
            description: "Glass, metal, paper and other recyclables",
            image: "/recycling-bin.webp",
            alt: "Recyclables collected during junk removal in Partille and driven to Öjersjö recycling centre",
          },
          {
            name: "Boxes",
            description: "Moving boxes and cardboard boxes",
            image: "/box.webp",
            alt: "Moving boxes removed from a villa in Öjersjö, Partille",
          },
          {
            name: "Furniture",
            description: "Living room, bedroom, office and other home furniture",
            image: "/furniture.webp",
            alt: "Sofas and home furniture during junk removal in Partille centrum",
          },
          {
            name: "Electronics",
            description: "TVs, computers, sound systems and other electronics",
            image: "/electronics.webp",
            alt: "Electronics and TVs removed in Sävedalen, Partille",
          },
          {
            name: "Appliances",
            description: "Fridges, washing machines, dryers and other appliances",
            image: "/appliances.webp",
            alt: "Fridges and appliances collected during junk removal in Björndammen, Partille",
          },
          {
            name: "Fitness Equipment",
            description: "Treadmills, bikes, weights and other exercise equipment",
            image: "/fitness-equipment.webp",
            alt: "Treadmill and fitness equipment during junk removal in Jonsered, Partille",
          },
        ],
      },
      seoContent: {
        mainHeading: "Junk removal in Partille – fast, hassle-free and responsible",
        mainText:
          "Partille is a small but densely populated municipality just northeast of Gothenburg, with four districts – Partille, Sävedalen, Jonsered and Öjersjö. The housing is mixed: the apartment quarters around Partille centrum, Björndammen and Furulund, the villa areas of Öjersjö and Sävedalen, and the historic mill setting of Jonsered by Lake Aspen. That mix makes junk removal a different challenge: in an apartment it is often stairwells and elevators, while in a villa it is more about a full garage or a basement storage room. With Ungdomskraft, you skip the hassle either way. We send young, strong workers with a van directly to your address.\n\nOur team handles the entire chain: we carry items out of your apartment, villa or storage unit, load them into the van and drive to the nearest recycling centre. Partille has a big advantage here – the municipality's own recycling centre in Öjersjö, on Landvettervägen, is located in the middle of the municipality and the waste is collected by Renova. This means the waste rarely has to be driven far. You don't need to worry about sorting, parking or heavy lifting. We take care of everything.\n\nBecause the E20 runs straight through Partille and the commuter train between Gothenburg and Alingsås stops at Partille station, we move quickly between districts. This means we can often be on site the same day you book. Our workers are used to both the apartment quarters around the Allum shopping centre and the narrow villa streets of Öjersjö and Jonsered – and have the right equipment: carrying belts, protective blankets and loading ramps that protect both your belongings and the stairwell throughout the process.",
        preparationHeading: "Preparing for your junk removal in Partille",
        preparationText:
          "You don't really need to do much at all – our young workers handle the heavy work. But if you want to speed things up and keep the cost down, there are a few simple things you can do:\n\nClear a path (about 1 metre wide) from the entrance to the items that need to go. If you live in a villa in Öjersjö, Jonsered, Sävedalen or Ugglum, it helps if we can reverse onto the driveway – let us know if a gate or garage door needs opening. If you live in an apartment in Partille centrum, Björndammen or Furulund, check whether there is a loading zone, courtyard or visitor parking near the entrance we can use.\n\nGather the items in one place if possible, for example in the hallway, garage or storage unit. You don't need to disassemble furniture – we include that in our service if needed. We cannot take hazardous waste such as paint, batteries and chemicals, but as a Partille resident you can drop those off for free at Öjersjö recycling centre.",
        additionalSections: [
          {
            heading: "Districts and areas we cover in Partille",
            content:
              "We perform junk removal across the entire municipality of Partille – from the apartment quarters to the villa areas and the mill settings. Whether you live in an apartment in Partille centrum, a villa in Öjersjö, a townhouse in Sävedalen or a house in Jonsered, we can be on site, often the same day.\n\nIn Partille centrum – around the Allum shopping centre, Björndammen and Furulund – we are highly experienced with apartment pickups via elevator and stairwell, close to the station and bus terminal. In Sävedalen, which borders Gothenburg, we meet a mix of villas and apartment buildings.\n\nWe are just as quick in the eastern and southern parts of the municipality: Jonsered with its historic mill setting and the Jonsered factories by Lake Aspen, and Öjersjö with villa housing near Lake Kåsjön, as well as Lexby, Ugglum and Kåhög. Thanks to the proximity of the E20 and the commuter train, we always plan the route in advance so you don't have to wait. No extra charges for any district.",
          },
          {
            heading: "Recycling and environmental responsibility in Partille",
            content:
              "An important part of our junk removal in Partille is that everything is handled in an environmentally responsible way. We sort the materials and drive them to approved recycling centres. Partille has a big advantage because the municipality has its own station in the middle of the municipality:\n\nÖjersjö recycling centre on Landvettervägen – Partille's own recycling centre, which receives sorted bulky waste, e-waste, packaging and hazardous waste, as well as items for reuse. Waste collection in the municipality is handled by Renova. Because Öjersjö is centrally located in the municipality, we keep travel time – and therefore the cost – down.\n\nItems that can be reused are donated to organisations like Stadsmissionen and Emmaus. This way, your junk removal contributes to a circular economy – items get a new life instead of ending up in landfill.\n\nIf the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Standard junk removal where everything goes to a recycling station does not qualify. The deduction is handled directly on the invoice.",
          },
          {
            heading: "Why book junk removal in Partille with Ungdomskraft?",
            listItems: [
              {
                label: "Local knowledge –",
                text: "We know Partille's districts, from the apartment quarters around Allum and Björndammen to the villa plots of Öjersjö and Jonsered, and that Öjersjö recycling centre is centrally located. This lets us plan efficient routes and keep the cost down for you.",
              },
              {
                label: "Fast availability –",
                text: "We have vans and staff across the Gothenburg region and the E20 runs straight through Partille. We can often be on site the same day you book – including evenings and weekends.",
              },
              {
                label: "Fixed price with no surprises –",
                text: "You see the price immediately when booking. No hidden fees, no surcharges for any district in Partille, and no quote requests. Book in two minutes.",
              },
              {
                label: "Insured and safe –",
                text: "All jobs are insured through Trygg Hansa. Your stairwell, your driveway, your walls and your belongings are protected throughout the entire process.",
              },
              {
                label: "Eco-friendly handling –",
                text: "We sort and recycle everything we can via Öjersjö recycling centre. Items suitable for donation are passed on. You may be entitled to a RUT tax deduction for transport to reuse.",
              },
            ],
          },
        ],
      },
      faq: [
        {
          question: "How much does junk removal in Partille cost?",
          answer:
            "The price depends on volume, weight and time. Our smallest category (Extra Liten) starts at 249 kr base fare, and an average job costs around 499–1,699 kr depending on the amount. You see the exact price immediately when booking – no hidden fees and no surcharges for any district in Partille.",
        },
        {
          question: "Can I get a RUT tax deduction on junk removal in Partille?",
          answer:
            "If the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Standard junk removal where everything goes to a recycling station does not qualify. The deduction is handled directly on the invoice.",
        },
        {
          question: "How quickly can you come for junk removal in Partille?",
          answer:
            "We have vans and staff across the Gothenburg region and the E20 runs straight through Partille, so we move quickly between districts. We can often be on site the same day, and for urgent needs sometimes within 60 minutes. Book online and choose the time that suits you.",
        },
        {
          question: "Is a van included with junk removal in Partille?",
          answer:
            "Yes, we always come with a van and crew. You don't need to arrange your own vehicle – we handle everything from carrying to transport and disposal at the nearest recycling centre.",
        },
        {
          question: "Can you carry items down from an apartment via stairs in Partille?",
          answer:
            "Absolutely. We are used to both stairwells and elevators in the apartment buildings around Partille centrum, Björndammen and Furulund. We carry furniture, appliances and other items regardless of floor – no extra charges for stairs.",
        },
        {
          question: "Do you collect from villas and garages in Partille?",
          answer:
            "Yes, a large share of our jobs in Partille are villa pickups in Öjersjö, Jonsered, Sävedalen and Ugglum. We reverse onto the driveway where possible and clear garages, basements and storage rooms. Just let us know if a gate or garage door needs opening.",
        },
        {
          question: "Can you disassemble furniture before removal?",
          answer:
            "Yes, we disassemble furniture on site if needed to get it through doors or down stairs. This is included in the service at no extra cost.",
        },
        {
          question: "Is junk removal in Partille insured?",
          answer:
            "Yes, all jobs are insured through Trygg Hansa. This means your property, stairwell, driveway and belongings are protected throughout the entire process – from lifting to transport.",
        },
        {
          question: "Where do you take the waste after junk removal in Partille?",
          answer:
            "We sort all materials and drive them to the nearest approved recycling centre. Partille has its own station, Öjersjö recycling centre on Landvettervägen, and waste collection is handled by Renova. Items that can be reused are donated to Stadsmissionen or Emmaus.",
        },
        {
          question: "Can I book junk removal in Partille on weekends?",
          answer:
            "Yes, we offer junk removal every day of the week, including Saturdays and Sundays. Choose the date and time that works best for you directly in our booking.",
        },
        {
          question: "Can you collect items from basements or storage units in Partille?",
          answer:
            "Yes, we pick up items from all spaces – basements, storage rooms, attics and garages. We are used to both the villa storage rooms in Öjersjö and Jonsered and the apartment storage units in Partille centrum and Björndammen.",
        },
        {
          question: "Can you make multiple stops during junk removal in Partille?",
          answer:
            "Yes, you can add multiple pickup addresses in the same booking. This is practical if you need to collect items from both your apartment in Partille centrum and a storage unit at another address in the municipality, for example.",
        },
        {
          question: "Which districts in Partille do you cover?",
          answer:
            "We perform junk removal across all of Partille – Partille centrum, Björndammen, Furulund, Sävedalen, Jonsered and Öjersjö, as well as Lexby, Ugglum and Kåhög. No extra charges for any district.",
        },
        {
          question: "Do you handle hazardous waste during junk removal in Partille?",
          answer:
            "Unfortunately, we cannot transport hazardous waste such as paint, chemicals, batteries or asbestos. As a Partille resident you can drop those off for free at Öjersjö recycling centre.",
        },
        {
          question: "Which municipalities around Partille do you serve?",
          answer:
            "We operate across the entire Gothenburg region, including Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö. No extra charges for surrounding municipalities.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.bortforsling,
      pricingRows: getPackagePricingRows("bortforsling", "en"),
      dynamicHeadings: {
        threeSteps: "Book your junk removal in Partille in 3 easy steps",
        step2Text:
          "Our young workers in Partille come to you and collect whatever you want removed. We handle heavy lifting, tight staircases, narrow elevators and full villa garages – you don't have to lift a finger.",
        ctaHeading: "Book junk removal in Partille today",
        ctaText:
          "Fill in the form and get an instant price – no quote requests. Easy junk removal across the whole municipality of Partille.",
      },
    },
  },
];
